import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Package, MapPin, MessageCircle, User as UserIcon, Truck } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { useCurrency } from "@/context/CurrencyContext";

export const AccountPage = () => {
  const { me, loading, logout, client } = useCustomerAuth();
  const nav = useNavigate();
  const { format } = useCurrency();
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);

  useEffect(() => {
    if (!loading && !me) nav("/account/login?next=/account", { replace: true });
  }, [me, loading, nav]);

  useEffect(() => {
    if (!me) return;
    (async () => {
      try {
        const [{ data: o }, { data: a }] = await Promise.all([
          client.get("/customer/orders"),
          client.get("/customer/addresses"),
        ]);
        setOrders(o || []);
        setAddresses(a || []);
      } catch (e) { /* noop */ }
    })();
  }, [me, client]);

  if (loading || !me) return null;

  const doLogout = async () => {
    await logout();
    toast.success("Signed out");
    nav("/", { replace: true });
  };

  return (
    <main data-testid="account-page" className="relative min-h-screen bg-white">
      <Seo path="/account" title="My Account" noindex />
      <AnnouncementBar />
      <Navbar />

      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-12 gap-8">
          {/* Profile card */}
          <aside className="col-span-12 md:col-span-4">
            <div className="border border-hairline bg-cream/50 p-6">
              <div className="flex items-center gap-4">
                {me.picture ? (
                  <img src={me.picture} alt="" loading="lazy" decoding="async" className="w-14 h-14 rounded-full object-cover border border-hairline" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-forest text-champagne flex items-center justify-center overline">
                    {(me.name || me.email || "").slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-display text-forest text-xl leading-none truncate">{me.name || "—"}</div>
                  <div className="text-forest/60 text-xs mt-1 truncate">{me.email}</div>
                </div>
              </div>
              <ul className="mt-6 space-y-2 text-forest/70 text-sm">
                <li className="flex items-center gap-2 overline text-[10px] text-forest/50">
                  <UserIcon size={11} strokeWidth={1.6} /> Signed in via {(me.provider || []).join(" · ")}
                </li>
              </ul>
              <button
                onClick={doLogout}
                data-testid="account-signout"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 overline text-forest border border-hairline rounded-full py-2 hover:bg-white transition-colors"
              >
                <LogOut size={12} strokeWidth={1.6} /> Sign out
              </button>
            </div>

            <div className="mt-4 border border-hairline p-4 text-forest/70 text-sm bg-white">
              <div className="overline text-gold mb-2 flex items-center gap-2">
                <MessageCircle size={11} strokeWidth={1.6} /> Need something bespoke?
              </div>
              <p className="leading-relaxed">Ping the atelier — quotes usually land within an hour.</p>
              <Link to="/shop" className="edit-link inline-block mt-2 text-xs">Open catalogue →</Link>
            </div>
          </aside>

          {/* Orders */}
          <div className="col-span-12 md:col-span-8 space-y-8">
            <div>
              <div className="flex items-baseline justify-between gap-3 mb-4">
                <div>
                  <div className="overline text-gold">§ Orders</div>
                  <h2 className="font-display text-forest text-3xl md:text-4xl leading-none">Your order history</h2>
                </div>
                <Link to="/shop" data-testid="account-shop-link" className="overline text-forest border border-forest/40 rounded-full px-4 py-2 hover:bg-forest hover:text-white">Reorder →</Link>
              </div>
              {orders.length === 0 ? (
                <div className="border border-hairline bg-cream/40 p-8 text-center text-forest/60">
                  <Package size={22} strokeWidth={1.4} className="mx-auto text-forest/40" />
                  <p className="mt-3 text-sm">You haven't placed any orders yet.</p>
                  <Link to="/shop" className="edit-link mt-3 inline-block">Browse the catalogue →</Link>
                </div>
              ) : (
                <ul className="border border-hairline divide-y divide-hairline">
                  {orders.map((o, i) => (
                    <li key={o.order_id || o._id || i} data-testid={`account-order-${i}`} className="p-4 md:p-5 flex items-start gap-4 bg-white">
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-forest text-lg leading-none">Order #{(o.order_id || "").slice(-8) || (i + 1)}</div>
                        <div className="overline text-forest/50 mt-1 text-[10px]">
                          {(o.created_at || "").slice(0, 10)} · {o.fulfillment_status || o.status || "received"} · {(o.items || []).length} item(s)
                        </div>
                        <div className="mt-2 text-forest/70 text-sm truncate">
                          {(o.items || []).map((it) => it.title).slice(0, 3).join(" · ")}
                        </div>
                        {(o.tracking_number || o.order_id) && (
                          <Link
                            to={`/track-order?q=${encodeURIComponent(o.tracking_number || o.order_id)}`}
                            data-testid={`account-order-track-${i}`}
                            className="mt-3 inline-flex items-center gap-1.5 overline text-[10px] tracking-[2px] text-forest border border-forest/40 rounded-full px-3 py-1.5 hover:bg-forest hover:text-white transition-colors"
                          >
                            <Truck size={11} strokeWidth={1.6} /> Track
                          </Link>
                        )}
                      </div>
                      <div className="font-display text-forest tabnum text-lg shrink-0">
                        {format(o.total || 0)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <div className="overline text-gold">§ Addresses</div>
              <h2 className="font-display text-forest text-3xl md:text-4xl leading-none mt-1">Saved shipping addresses</h2>
              {addresses.length === 0 ? (
                <div className="mt-4 border border-hairline bg-cream/40 p-8 text-center text-forest/60">
                  <MapPin size={22} strokeWidth={1.4} className="mx-auto text-forest/40" />
                  <p className="mt-3 text-sm">Addresses from your orders will show up here for one-tap reuse at checkout.</p>
                </div>
              ) : (
                <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {addresses.map((a, i) => (
                    <li key={i} data-testid={`account-address-${i}`} className="border border-hairline p-4 bg-white text-forest/80 text-sm">
                      <div className="font-display text-forest text-lg leading-none mb-1">{a.name || me.name}</div>
                      <div>{a.line1}</div>
                      {a.line2 && <div>{a.line2}</div>}
                      <div>{[a.city, a.state, a.postal].filter(Boolean).join(", ")}</div>
                      <div className="overline text-forest/50 text-[10px] mt-2">{a.country}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};
